import { useMemo, useState, useEffect, useRef } from 'react';
import { IMenuDomain } from '../index';
import IconMi from 'components/common/icon/IconMi';
import IconHandler from 'components/common/icon/IconHandler';
import { Box, Divider, Stack, Typography } from '@mui/material';
import ExtraMenu from './ExtraMenu';
import { mergeCustomStyles } from 'app/concierge/tools/mergeCustomStyles';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from '../styles';
import { useSelector } from 'react-redux';
import AuthRequiredPopup from './AuthRequiredPopup';
import { useHasTokenString } from 'hooks/useHasTokenString';
import { IDomain } from 'store/conciergeSlice';
import { useConfigConstantValue } from 'hooks/useConfigConstant';
import { isSameSiteHttpUrl } from 'tools/safeHtml';

function GroupHeader({
    domain,
    styles,
    onToggle,
    isCollapsed,
}: {
    domain: IMenuDomain;
    styles: typeof defaultStyles;
    onToggle?: () => void;
    isCollapsed?: boolean;
}) {
    return (
        <Box sx={styles.groupHeader} onClick={onToggle}>
            <span>{domain.title}</span>
            <IconMi
                icon="chevron-down"
                sx={{
                    ...styles.groupHeaderChevron,
                    transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                }}
            />
        </Box>
    );
}

export default function DomainMenuItem({
    domain,
    selectDomain,
    onSelectPrompt,
    closeMenu,
    onToggleGroup,
    isGroupCollapsed,
}: {
    domain: IMenuDomain;
    selectDomain: (name: string) => void;
    onSelectPrompt: (domain: string, prompt: string) => void;
    closeMenu: () => void;
    onToggleGroup?: (title: string) => void;
    isGroupCollapsed?: boolean;
}) {
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;
    const roothome = useConfigConstantValue('ROOTHOME') as string | undefined;
    const availableDomains = useSelector((state: any) => state.concierge.availableDomains) as IDomain[];

    const styles = useMemo(() => {
        return mergeCustomStyles(defaultStyles, customStyles?.footerDomainSelector, 'menuItem');
    }, [customStyles]);

    // All hooks must be called unconditionally before any early return
    const [showAuthPopup, setShowAuthPopup] = useState(false);
    const [waitingForAuth, setWaitingForAuth] = useState(false);
    const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
    const tokenFieldName = domain.authInfo?.tokenFieldName || '';
    const tokenStatus = useHasTokenString(tokenFieldName);
    const previousTokenStatus = useRef(tokenStatus.hasToken);
    const needsAuth = !!domain.hasAuth && !tokenStatus.hasToken;

    useEffect(() => {
        if (waitingForAuth && !previousTokenStatus.current && tokenStatus.hasToken) {
            setWaitingForAuth(false);
            if (pendingPrompt) {
                onSelectPrompt(domain.name, pendingPrompt);
                setPendingPrompt(null);
            } else {
                selectDomain(domain.name);
            }
        }
        previousTokenStatus.current = tokenStatus.hasToken;
    }, [tokenStatus.hasToken, waitingForAuth, domain.name, selectDomain, pendingPrompt, onSelectPrompt]);

    if (domain.isDivider) {
        return <Divider sx={{ ml: '37px', mr: 0, my: 0.5 }} />;
    }

    if (domain.isGroupHeader) {
        return (
            <GroupHeader
                domain={domain}
                styles={styles}
                onToggle={() => onToggleGroup?.(domain.title)}
                isCollapsed={isGroupCollapsed}
            />
        );
    }

    const className = (domain.menuSelected ? 'selected' : '') + (domain.header ? ' header' : '');

    const iconProps = {
        type: domain.icon_ind,
        value: domain.icon_ind === 'upload' ? `data:image/png;base64,${domain.icon_data}` : domain.icon_name,
    };

    const onDomainClick = (event: React.MouseEvent) => {
        if (needsAuth) {
            (event as any).__miCloseDropdown = true;
            setShowAuthPopup(true);
        } else {
            selectDomain(domain.name);
        }
    };

    const handleAuthorize = () => {
        setShowAuthPopup(false);
        // Open only this site's own sign-in address (security unit 7, D3; the rule lives in isSameSiteHttpUrl).
        if (domain.authInfo?.authUrl && isSameSiteHttpUrl(domain.authInfo.authUrl, roothome)) {
            setWaitingForAuth(true);
            window.open(domain.authInfo?.authUrl, `Authentication`, 'width=530,height=530');
        }
    };

    const handleClosePopup = () => {
        setShowAuthPopup(false);
        setWaitingForAuth(false);
        setPendingPrompt(null);
    };

    const handleAuthClick = () => {
        setShowAuthPopup(true);
    };

    const handleAuthPromptClick = (prompt: string) => {
        setPendingPrompt(prompt);
        setShowAuthPopup(true);
    };

    return (
        <>
            <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
                sx={styles.menuItem}
                className={className}
                onClick={onDomainClick}
            >
                <Stack direction={'row'} gap={1} sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                    <Box
                        width={'21px'}
                        height={'21px'}
                        display="flex"
                        justifyContent={'center'}
                        alignItems={'center'}
                        flexShrink={0}
                    >
                        {needsAuth ? (
                            <Box sx={styles.noCheck} />
                        ) : domain.menuSelected ? (
                            <IconMi icon={'check'} sx={styles.checkIcon} />
                        ) : (
                            <Box sx={{ width: '16px' }} />
                        )}
                    </Box>
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        spacing={1}
                        justifyContent={'flex-start'}
                        sx={{ minWidth: 0, overflow: 'hidden' }}
                    >
                        <Box
                            sx={{
                                width: '21px',
                                height: '21px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <IconHandler icon={iconProps} sx={{ fontSize: '16px' }} />
                        </Box>

                        <Typography noWrap>{domain.title}</Typography>
                    </Stack>
                </Stack>
                <ExtraMenu
                    domain={domain}
                    onSelectPrompt={onSelectPrompt}
                    needsAuth={needsAuth}
                    closeMenu={closeMenu}
                    onAuthClick={handleAuthClick}
                    onAuthPromptClick={handleAuthPromptClick}
                />
            </Stack>

            <AuthRequiredPopup
                open={showAuthPopup}
                onConfirm={handleAuthorize}
                onHide={handleClosePopup}
                domainTitle={domain.title}
                authInfo={domain.authInfo}
                serviceName={domain.serviceName}
                availableDomains={availableDomains}
            />
        </>
    );
}
